import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import useVoyagerStore from '../../stores/useVoyagerStore';
import type { LandingAgentTurn, PriorWorkLine, PriorWorkMatch, PriorWorkRefreshed, PriorWorkVerdict, PriorWorkWindow } from '../../stores/slices/landingAgentSlice';
import { stromaAnswerCard, stromaInset, stromaSectionLabel } from './askPresentation';

const KIND_LABELS: Record<string, string> = { thread: 'Thread', doc: 'Review', report: 'Saved report' };
/** How many then-vs-now lines the strip shows; every pair stays in the answer's record. */
export const SHOWN_LINES = 10;

export function windowLabel(w: PriorWorkWindow | null | undefined): string {
  if (!w?.start || !w?.end) return '';
  return w.start === w.end ? w.start : `${w.start} – ${w.end}`;
}

/** The Refresh button's words (#762, owner decision A): the window it runs for, and — BEFORE the click — the
 *  perspective it runs in whenever that is not the asker's pick (or the pick is Auto). */
export function refreshLabel(match: PriorWorkMatch, pick: string | null | undefined, names: Record<string, string>): string {
  const refresh = match.refresh;
  if (!refresh) return '';
  const label = `Refresh for ${windowLabel(refresh.window)}`;
  return !pick || pick === 'auto' || pick !== refresh.perspective ? `${label} · in ${names[refresh.perspective] || refresh.perspective}` : label;
}

function whose(m: { mine?: boolean; by?: string | null }): string {
  return m.mine ? 'You' : m.by || 'Shared in your workspace';
}

function VerdictLine({ verdict }: { verdict?: PriorWorkVerdict | null }) {
  if (!verdict?.headline) return null;
  const hidden = verdict.policy?.hidden_count || 0;
  return (
    <Typography variant="body2" data-testid="prior-work-verdict" sx={{ mt: 0.5 }}>
      {`Concluded: ${verdict.headline}`}
      {hidden > 0 && <Typography component="span" variant="caption" sx={{ ml: 0.75, color: 'text.secondary' }}>
        {`${hidden} hidden at your evidence bar`}
      </Typography>}
    </Typography>
  );
}

/** One earlier analysis: where it lives, whose, when, and the ways to it (Start fresh is once, for the card). On a
 *  list asked for explicitly (#911) it also says what the analysis found, as disclosed to this reader. */
export function Match({ match, refresh, onRefresh, done }: { match: PriorWorkMatch; refresh: string; onRefresh: () => void; done: boolean }) {
  const then = windowLabel(match.window);
  return (
    <Box data-testid="prior-work-match" sx={{ p: 1.5, ...stromaInset }}>
      <Typography variant="caption" sx={{ color: 'var(--ink-3)' }}>
        {[whose(match), KIND_LABELS[match.kind || ''] || match.kind, match.when].filter(Boolean).join(' · ')}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>{match.question || match.title}</Typography>
      {then && <Typography variant="caption" sx={{ display: 'block', color: 'var(--ink-2)' }}>
        {`Answered for ${then}`}
      </Typography>}
      <VerdictLine verdict={match.verdict} />
      {!match.verdict?.headline && match.found && (
        <Typography variant="body2" data-testid="prior-work-found" sx={{ mt: 0.5, overflowWrap: 'anywhere' }}>{`Found: ${match.found}`}</Typography>
      )}
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
        {refresh && <Button size="small" variant="contained" disabled={done} onClick={onRefresh} sx={{ textTransform: 'none' }}>
          {refresh}
        </Button>}
        <Button size="small" variant="outlined" component="a" href={match.href} sx={{ textTransform: 'none' }}>Open the original</Button>
      </Box>
    </Box>
  );
}

function Offer({ turn, matches }: { turn: LandingAgentTurn; matches: PriorWorkMatch[] }) {
  const send = useVoyagerStore((s) => s.sendLandingAgentQuery);
  const perspectives = useVoyagerStore((s) => s.perspectivesList);
  const [done, setDone] = React.useState(false);
  const reask = turn.askedRequest;
  const names = Object.fromEntries((perspectives || []).map((p) => [p.id, p.name || p.id]));
  const refresh = (m: PriorWorkMatch) => {
    if (!reask || !m.refresh || m.ref.source === 'report' || !m.ref.turn_id) return;
    setDone(true);
    send(reask.query, { reask, prior: { refresh: { source: m.ref.source, id: m.ref.id, turn_id: m.ref.turn_id } } });
  };
  const fresh = () => {
    if (!reask) return;
    setDone(true);
    send(reask.query, { reask, prior: 'fresh' });
  };
  const anyRefresh = matches.some((m) => m.refresh);
  return (
    <Paper variant="outlined" data-testid="prior-work-offer" sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.25, ...stromaAnswerCard }}>
      <Typography sx={stromaSectionLabel}>Prior work</Typography>
      <Typography variant="body2">
        {matches.length === 0 ? 'The earlier analysis offered here is no longer available to you.'
          : `${matches.length === 1 ? 'This has been looked at before.' : 'This has been looked at before, more than once.'} `
            + (anyRefresh ? 'Refresh it for a newer window, open the original, or start fresh.' : 'Open the original, or start fresh.')}
      </Typography>
      {matches.map((m) => (
        <Match key={`${m.ref.source}:${m.ref.id}:${m.ref.turn_id}`} match={m} done={done || !reask}
               refresh={reask ? refreshLabel(m, reask.perspective_id, names) : ''} onRefresh={() => refresh(m)} />
      ))}
      {reask && <Box><Button size="small" variant="text" disabled={done} onClick={fresh} sx={{ textTransform: 'none' }}>Start fresh</Button></Box>}
    </Paper>
  );
}

/** Start fresh (#762, #911): the same request again, the prior-work step skipped — once. */
function StartFresh({ turn }: { turn: LandingAgentTurn }) {
  const send = useVoyagerStore((s) => s.sendLandingAgentQuery);
  const [done, setDone] = React.useState(false);
  const reask = turn.askedRequest;
  if (!reask) return null;
  const fresh = () => {
    setDone(true);
    send(reask.query, { reask, prior: 'fresh' });
  };
  return <Button size="small" variant="text" disabled={done} onClick={fresh} sx={{ textTransform: 'none' }}>Start fresh</Button>;
}

/** What a Refresh re-ran and what changed since: then vs now, above the answer it produced. A repeat (#911 part C) —
 *  the same question asked again, answered from the earlier analysis — says what it is based on, the window it now
 *  covers (never the earlier numbers as today's), and offers Start fresh. */
function Refreshed({ refreshed, turn }: { refreshed: PriorWorkRefreshed; turn: LandingAgentTurn }) {
  const source = refreshed.source;
  const repeat = refreshed.basis === 'repeat';
  const lines = refreshed.lines || [];
  const gone = refreshed.gone_lines || [];
  const shown = lines.slice(0, SHOWN_LINES);
  const notComparable = refreshed.not_comparable || 0;
  const sx = { p: 1.5, ...stromaInset };
  const covers = repeat && refreshed.covers
    ? <Typography variant="caption" data-testid="prior-work-covers" sx={{ display: 'block' }}>{refreshed.covers}</Typography> : null;
  if (!source?.href) {   // no longer open to this asker: the server sent no source at all
    return (
      <Box data-testid="prior-work-refreshed" sx={sx}>
        {covers}
        <Typography variant="caption">{refreshed.summary}</Typography>
        {repeat && <Box><StartFresh turn={turn} /></Box>}
      </Box>
    );
  }
  return (
    <Box data-testid="prior-work-refreshed" sx={sx}>
      {repeat ? (
        <Typography variant="body2" data-testid="prior-work-based-on" sx={{ fontWeight: 600 }}>{refreshed.based_on}</Typography>
      ) : null}
      {covers}
      <Typography variant="caption" sx={{ display: 'block', color: 'var(--ink-3)' }}>
        {(repeat ? `Asked as “${source.question || source.title}”` : `Refreshed from “${source.question || source.title}” (${whose(source)}${source.when ? `, ${source.when}` : ''})`)
          + (refreshed.then_window ? ` · ${windowLabel(refreshed.then_window)} → ${windowLabel(refreshed.now_window)}` : '')}
      </Typography>
      {shown.length > 0 ? (
        <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
          {shown.map((l) => <Typography key={l} component="li" variant="body2">{l}</Typography>)}
        </Box>
      ) : <Typography variant="body2" sx={{ mt: 0.5 }}>{refreshed.summary}</Typography>}
      {lines.length > shown.length && (
        <Typography variant="caption" data-testid="prior-work-more">{`and ${lines.length - shown.length} more`}</Typography>
      )}
      {gone.length > 0 && (
        <Typography variant="caption" data-testid="prior-work-gone" sx={{ display: 'block' }}>
          {`No longer present: ${gone.length === 1 ? gone[0] : `${gone.length} earlier rows (${gone.slice(0, 3).join('; ')}${gone.length > 3 ? '; …' : ''})`}`}
        </Typography>
      )}
      {notComparable > 0 && (
        <Typography variant="caption" data-testid="prior-work-not-comparable" sx={{ display: 'block', color: 'text.secondary' }}>
          {`${notComparable} earlier ${notComparable === 1 ? 'number' : 'numbers'} could not be compared number for number: `
            + 'a part of the earlier answer this Refresh did not re-run, another window, or a grouping that names no single number'}
        </Typography>
      )}
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Button size="small" variant="text" component="a" href={source.href} sx={{ textTransform: 'none', px: 0 }}>Open the original</Button>
        {repeat && <StartFresh turn={turn} />}
      </Box>
    </Box>
  );
}

/** The earlier work an answer that ran fresh names (#911 part C): one line, with Open — and, for the same question
 *  that was not answered from, why it ran fresh. Only what the server read for this asker now is shown. */
function Related({ lines }: { lines: PriorWorkLine[] }) {
  const [line, ...rest] = lines;
  if (!line) return null;
  const sx = { p: 1.5, ...stromaInset };
  return (
    <Box data-testid="prior-work-related" sx={{ ...sx, display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
      <Typography variant="body2" sx={{ overflowWrap: 'anywhere', flex: '1 1 auto' }}>{line.text}</Typography>
      <Button size="small" variant="text" component="a" href={line.href} sx={{ textTransform: 'none', px: 0 }}>Open</Button>
      {line.why && <Typography variant="caption" data-testid="prior-work-why" sx={{ display: 'block', width: '100%' }}>{line.why}</Typography>}
      {rest.length > 0 && (
        <Typography variant="caption" data-testid="prior-work-related-more" sx={{ display: 'block', width: '100%', color: 'text.secondary' }}>
          {`and ${rest.length} more earlier ${rest.length === 1 ? 'analysis' : 'analyses'}`}
        </Typography>
      )}
    </Box>
  );
}

/** Prior work (#762, #911): the offer of an earlier analysis, the then-vs-now of a Refresh or of a repeat answered from
 *  its earlier analysis, or the line naming earlier work beside an answer that ran fresh — as the server read it for
 *  this asker (a reopened thread is read again by the server, so what they can no longer open is gone). */
export default function PriorWorkCard({ turn }: { turn: LandingAgentTurn }) {
  const prior = turn.priorWork;
  if (prior?.type === 'prior_work_refreshed') return <Refreshed refreshed={prior} turn={turn} />;
  if (prior?.type === 'prior_work_related') return <Related lines={(prior.lines || []).filter((l) => l.href && l.text)} />;
  if (prior?.type === 'prior_work_offered') return <Offer turn={turn} matches={(prior.matches || []).filter((m) => m.href)} />;
  return null;
}
